<template>
  <div class="stock-count-view">
    <div v-if="!venueId" class="no-venue-message">
      <i class="material-icons"> info_outline </i>

      <span> No active venue is available. </span>
    </div>

    <template v-else>
      <div class="count-toolbar">
        <div>
          <div class="count-heading-row">
            <div class="count-heading">Stock Count</div>

            <span v-if="draftCountId" class="draft-chip"> Saved Draft </span>
          </div>

          <div class="grey-text text-darken-1">
            {{ countStatusText }}
          </div>
        </div>

        <div class="toolbar-actions">
          <button
            v-if="countStarted && lines.length"
            class="btn blue-grey darken-2 waves-effect waves-light"
            type="button"
            :disabled="busy"
            @click="generateStockCountSheet"
          >
            <i class="material-icons left"> print </i>

            Generate Count Sheet
          </button>

          <button
            v-if="!countStarted"
            class="btn teal darken-2 waves-effect waves-light"
            type="button"
            :disabled="busy"
            @click="newCount"
          >
            <i class="material-icons left"> add </i>

            New Count
          </button>
        </div>
      </div>

      <div v-if="error || recipeError" class="error-box">
        {{ error || recipeError }}
      </div>

      <div v-if="successMessage" class="success-box">
        {{ successMessage }}
      </div>

      <div v-if="busyLoad" class="progress">
        <div class="indeterminate"></div>
      </div>

      <div v-else-if="!countStarted" class="count-empty">
        Press
        <strong> New Count </strong>
        to begin a new stock count.
      </div>

      <form v-else @submit.prevent="saveDraft">
        <div class="count-information">
          <i class="material-icons"> info_outline </i>

          <span>
            Opening Count, Stock In, Stock Out and Closing Count are entered in the
            product's Stock Unit. Direct Sales and Recipe Sales are Sales Units and are
            converted back into Stock Units automatically for variance.
          </span>
        </div>

        <StockRecipeSales
          :recipes="recipes"
          :model-value="recipeSaleQuantities"
          :disabled="busy"
          @update:model-value="recipeSalesChanged"
        />

        <div class="stock-count-table-wrap">
          <table class="striped highlight stock-count-table">
            <thead>
              <tr>
                <th>Product Name</th>

                <th>Supplier</th>

                <th>Opening Count</th>

                <th>Stock In</th>

                <th>Stock Out</th>

                <th>Closing Count</th>

                <th>Direct Sales</th>

                <th>Recipe Sales</th>

                <th class="right-align">Var (Units)</th>

                <th class="right-align">Var (£)</th>
              </tr>
            </thead>

            <tbody v-for="group in groupedLines" :key="group.name">
              <tr class="product-group-row">
                <td colspan="10">
                  <div class="product-group-header">
                    <div class="product-group-title">
                      <strong>
                        {{ group.name }}
                      </strong>

                      <span class="product-count">
                        {{ group.items.length }}
                        product{{ group.items.length === 1 ? "" : "s" }}
                      </span>
                    </div>

                    <button
                      class="btn-small group-add-button waves-effect waves-light"
                      type="button"
                      :disabled="busy"
                      @click="openAddLineModal(group.name)"
                    >
                      <i class="material-icons"> add </i>

                      <span> Add Line </span>
                    </button>
                  </div>
                </td>
              </tr>

              <tr v-if="!group.items.length" class="empty-group-row">
                <td colspan="10">
                  No products from this group are currently in the count. Use Add Line to
                  add one.
                </td>
              </tr>

              <tr v-for="line in group.items" :key="line.product_key">
                <td class="product-name">
                  <span>
                    {{ line.product_name }}
                  </span>

                  <span class="stock-unit-chip">
                    {{ line.stock_unit || "Stock Unit" }}
                  </span>
                </td>

                <td>
                  <select
                    v-model="line.product_id"
                    class="browser-default supplier-select"
                    @change="supplierChanged(line)"
                  >
                    <option value="" disabled>Select Supplier</option>

                    <option
                      v-for="variant in supplierOptionsForLine(line)"
                      :key="variant.id"
                      :value="variant.id"
                    >
                      {{ variant.supplier }}
                    </option>
                  </select>
                </td>

                <td>
                  <input
                    :value="formatInputNumber(line.opening_count)"
                    type="number"
                    step="0.01"
                    class="count-input readonly-input"
                    readonly
                  />
                </td>

                <td>
                  <input
                    v-model.number="line.stock_in"
                    type="number"
                    min="0"
                    step="0.01"
                    inputmode="decimal"
                    class="count-input"
                    :title="stockInTitle(line)"
                  />
                </td>

                <td>
                  <input
                    v-model.number="line.stock_out"
                    type="number"
                    min="0"
                    step="0.01"
                    inputmode="decimal"
                    class="count-input"
                    :title="stockOutTitle(line)"
                  />
                </td>

                <td>
                  <input
                    v-model.number="line.closing_count"
                    type="number"
                    min="0"
                    step="0.01"
                    inputmode="decimal"
                    class="count-input"
                  />
                </td>

                <td>
                  <input
                    v-model.number="line.sales"
                    type="number"
                    min="0"
                    step="0.01"
                    inputmode="decimal"
                    class="count-input sales-input"
                    :title="salesConversionTitle(line)"
                  />
                </td>

                <td>
                  <input
                    :value="formatInputNumber(line.recipe_sales)"
                    type="number"
                    step="0.01"
                    class="count-input readonly-input recipe-sales-input"
                    :title="recipeSalesTitle(line)"
                    readonly
                  />
                </td>

                <td
                  class="right-align variance-result"
                  :class="varianceClass(varianceUnits(line))"
                  :title="`Converted sales: ${formatNumber(salesStockUnits(line))} ${
                    line.stock_unit || 'stock units'
                  }`"
                >
                  {{ formatNumber(varianceUnits(line)) }}
                </td>

                <td
                  class="right-align variance-result"
                  :class="varianceClass(varianceValue(line))"
                >
                  {{ money(varianceValue(line)) }}
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <div class="count-footer">
          <div class="footer-actions">
            <button
              class="btn teal darken-2 waves-effect waves-light"
              type="submit"
              :disabled="busy || !lines.length || !countValid"
            >
              <i class="material-icons left"> save </i>

              {{ busySave ? "Saving..." : "Save Draft" }}
            </button>

            <button
              class="btn green darken-2 waves-effect waves-light"
              type="button"
              :disabled="busy || !lines.length || !countValid"
              @click="finaliseCount"
            >
              <i class="material-icons left"> lock </i>

              {{ busyFinalise ? "Finalising..." : "Finalise Count" }}
            </button>

            <button
              class="btn-flat waves-effect"
              type="button"
              :disabled="busy"
              @click="cancelCount"
            >
              {{ draftCountId ? "Discard Changes" : "Cancel" }}
            </button>
          </div>

          <div class="variance-key">
            <span class="green-text text-darken-2"> Positive = Stock Gain </span>

            <span class="red-text text-darken-2"> Negative = Stock Missing </span>
          </div>
        </div>

        <div v-if="hasImportedMovements" class="movement-info">
          <i class="material-icons"> swap_vert </i>

          <span>
            This count includes
            {{ deliveryNoteIds.length }}
            delivery note{{ deliveryNoteIds.length === 1 ? "" : "s" }}
            and
            {{ internalTransferIds.length }}
            internal transfer{{ internalTransferIds.length === 1 ? "" : "s" }}. Delivery
            Order Units have been converted into Stock Units using the Stock Database
            conversion.
          </span>
        </div>

        <div v-if="lines.length && !countValid" class="supplier-warning">
          <i class="material-icons"> warning </i>

          Every product must have a supplier and complete Order Unit / Stock Unit / Sales
          Unit conversion before the count can be saved or finalised.
        </div>
      </form>
    </template>

    <!-- =====================================================
         ADD LINE MODAL
         ===================================================== -->

    <div v-if="addLineModalOpen" class="modal-overlay" @click.self="closeAddLineModal">
      <div class="add-line-modal">
        <div class="modal-header">
          <div>
            <div class="modal-title">
              Add
              {{ addLineForm.product_group }}
              Product
            </div>

            <div class="grey-text text-darken-1">
              Select a product and supplier from the Stock Database.
            </div>
          </div>

          <button
            class="modal-close-button"
            type="button"
            title="Close"
            @click="closeAddLineModal"
          >
            <i class="material-icons"> close </i>
          </button>
        </div>

        <div v-if="!addLineProductOptions.length" class="no-products-message">
          <i class="material-icons"> info_outline </i>

          <span>
            All available
            {{ addLineForm.product_group }}
            products are already included in this count.
          </span>
        </div>

        <form @submit.prevent="addSelectedLine">
          <div class="modal-form-grid">
            <div class="modal-field">
              <label for="add-line-product"> Product Name </label>

              <select
                id="add-line-product"
                v-model="addLineForm.product_key"
                class="browser-default modal-select"
                required
                :disabled="!addLineProductOptions.length"
                @change="addLineProductChanged"
              >
                <option value="" disabled>Select Product</option>

                <option
                  v-for="product in addLineProductOptions"
                  :key="product.key"
                  :value="product.key"
                >
                  {{ product.product_name }}
                </option>
              </select>
            </div>

            <div class="modal-field">
              <label for="add-line-supplier"> Supplier </label>

              <select
                id="add-line-supplier"
                v-model="addLineForm.product_id"
                class="browser-default modal-select"
                required
                :disabled="!addLineForm.product_key"
              >
                <option value="" disabled>Select Supplier</option>

                <option
                  v-for="variant in addLineSupplierOptions"
                  :key="variant.id"
                  :value="variant.id"
                >
                  {{ variant.supplier }}
                </option>
              </select>
            </div>
          </div>

          <div v-if="selectedAddLineVariant" class="selected-product-details">
            <div>
              <span class="detail-label"> Order Unit </span>

              <strong>
                {{ selectedAddLineVariant.order_unit || "—" }}
              </strong>
            </div>

            <div>
              <span class="detail-label"> Stock Unit </span>

              <strong>
                {{ selectedAddLineVariant.stock_unit || "—" }}
              </strong>
            </div>

            <div>
              <span class="detail-label"> Stock / Order </span>

              <strong>
                {{ formatInputNumber(selectedAddLineVariant.stock_units_per_order_unit) }}
              </strong>
            </div>

            <div>
              <span class="detail-label"> Sales / Stock </span>

              <strong>
                {{ formatInputNumber(selectedAddLineVariant.sales_units_per_stock_unit) }}
              </strong>
            </div>

            <div>
              <span class="detail-label"> Order Unit Cost </span>

              <strong>
                {{ money(selectedAddLineVariant.unit_cost) }}
              </strong>
            </div>

            <div>
              <span class="detail-label"> Product Group </span>

              <strong>
                {{ selectedAddLineVariant.product_group }}
              </strong>
            </div>
          </div>

          <div class="modal-actions">
            <button
              class="btn teal darken-2 waves-effect waves-light"
              type="submit"
              :disabled="!addLineValid"
            >
              <i class="material-icons left"> add </i>

              Add Line
            </button>

            <button
              class="btn-flat waves-effect"
              type="button"
              @click="closeAddLineModal"
            >
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  </div>
</template>

<script>
import {
  fetchDraftStockCount,
  fetchLatestStockCount,
  fetchPendingStockDeliveryNotes,
  fetchPendingStockInternalTransfers,
  fetchStockProductsForCount,
  finaliseStockCount,
  saveStockCountDraft,
  subscribeStockProductsForCount,
} from "@/scripts/sqlApiHelper/stockCountApi";

import { fetchStockRecipes } from "@/scripts/sqlApiHelper/stockRecipesApi";

import { buildStockCountSheetPdf } from "@/scripts/stockPdfReport";

import StockRecipeSales from "@/components/stock/StockRecipeSales.vue";

const PRODUCT_GROUPS = [
  "Draught Beer",
  "Bottled Beer",
  "FaB's",
  "Vodka",
  "Rum",
  "Gin",
  "Whisky",
  "Brandy",
  "Tequila",
  "Liqueurs",
  "Red Wine",
  "White Wine",
  "Rose Wine",
  "Champagnes",
  "Draught Post Mix",
  "Soft Drinks",
];

function makeProductKey(productGroup, productName) {
  return [
    String(productGroup ?? "")
      .trim()
      .toLowerCase(),

    String(productName ?? "")
      .trim()
      .toLowerCase(),
  ].join("::");
}

function blankAddLineForm() {
  return {
    product_group: "",
    product_key: "",
    product_id: "",
  };
}

export default {
  name: "StockCountView",

  components: {
    StockRecipeSales,
  },

  props: {
    venueId: {
      type: String,
      default: "",
    },
  },

  data() {
    return {
      productGroups: PRODUCT_GROUPS,

      catalogue: [],
      catalogueUnsubscribe: null,

      recipes: [],
      recipeSaleQuantities: {},

      lines: [],
      previousCount: null,
      draftCountId: "",

      deliveryNoteIds: [],
      internalTransferIds: [],

      countStarted: false,

      busyLoad: false,
      busySave: false,
      busyFinalise: false,

      error: null,
      recipeError: null,
      successMessage: null,

      addLineModalOpen: false,
      addLineForm: blankAddLineForm(),
    };
  },

  computed: {
    busy() {
      return this.busyLoad || this.busySave || this.busyFinalise;
    },

    countValid() {
      return (
        this.lines.length > 0 &&
        this.lines.every(
          (line) =>
            !!line.product_id &&
            !!line.supplier &&
            !!line.order_unit &&
            !!line.stock_unit &&
            this.number(line.stock_units_per_order_unit) > 0 &&
            this.number(line.sales_units_per_stock_unit) > 0
        )
      );
    },

    countStatusText() {
      if (!this.venueId) {
        return "No venue selected.";
      }

      if (this.busyLoad) {
        return "Preparing stock count...";
      }

      if (this.countStarted) {
        const prefix = this.draftCountId ? "Saved draft — " : "";

        return (
          prefix +
          `${this.lines.length} product` +
          `${this.lines.length === 1 ? "" : "s"} ` +
          "in current count"
        );
      }

      return "Start a new venue stock count.";
    },

    hasImportedMovements() {
      return this.deliveryNoteIds.length > 0 || this.internalTransferIds.length > 0;
    },

    activeProductGroups() {
      const groupSet = new Set();

      for (const product of this.catalogue) {
        const group = String(product.product_group ?? "").trim();

        if (group) {
          groupSet.add(group);
        }
      }

      for (const line of this.lines) {
        const group = String(line.product_group ?? "").trim();

        if (group) {
          groupSet.add(group);
        }
      }

      const ordered = this.productGroups.filter((group) => groupSet.has(group));

      const otherGroups = Array.from(groupSet)
        .filter((group) => !this.productGroups.includes(group))
        .sort((a, b) =>
          a.localeCompare(b, "en", {
            sensitivity: "base",
          })
        );

      return [...ordered, ...otherGroups];
    },

    groupedLines() {
      return this.activeProductGroups.map((groupName) => ({
        name: groupName,

        items: this.lines
          .filter((line) => line.product_group === groupName)
          .sort((a, b) =>
            String(a.product_name ?? "").localeCompare(
              String(b.product_name ?? ""),
              "en",
              {
                sensitivity: "base",
              }
            )
          ),
      }));
    },

    addLineProductOptions() {
      if (!this.addLineForm.product_group) {
        return [];
      }

      return this.availableProductsForGroup(this.addLineForm.product_group);
    },

    addLineSupplierOptions() {
      if (!this.addLineForm.product_key) {
        return [];
      }

      return this.catalogue
        .filter(
          (variant) =>
            makeProductKey(variant.product_group, variant.item_name) ===
            this.addLineForm.product_key
        )
        .sort((a, b) =>
          String(a.supplier ?? "").localeCompare(String(b.supplier ?? ""), "en", {
            sensitivity: "base",
          })
        );
    },

    selectedAddLineVariant() {
      if (!this.addLineForm.product_id) {
        return null;
      }

      return (
        this.catalogue.find((product) => product.id === this.addLineForm.product_id) ||
        null
      );
    },

    addLineValid() {
      const variant = this.selectedAddLineVariant;

      return Boolean(
        this.addLineForm.product_group &&
          this.addLineForm.product_key &&
          variant &&
          variant.order_unit &&
          variant.stock_unit &&
          this.number(variant.stock_units_per_order_unit) > 0 &&
          this.number(variant.sales_units_per_stock_unit) > 0
      );
    },
  },

  watch: {
    venueId: {
      immediate: true,

      async handler(newVenueId) {
        this.stopCatalogueSubscription();

        this.clearCountState();

        if (!newVenueId) {
          return;
        }

        this.startCatalogueSubscription();

        await this.loadExistingDraft();
      },
    },
  },

  beforeUnmount() {
    this.stopCatalogueSubscription();
  },

  methods: {
    number(value) {
      const n = Number(value);

      return Number.isFinite(n) ? n : 0;
    },

    stockUnitCost(line) {
      const orderCost = this.number(line.unit_cost_order_unit ?? line.unit_cost);

      const stockPerOrder = this.number(line.stock_units_per_order_unit);

      if (stockPerOrder <= 0) {
        return 0;
      }

      return orderCost / stockPerOrder;
    },

    async generateStockCountSheet() {
      if (!this.lines.length || this.busy) {
        return;
      }

      this.error = null;
      this.successMessage = null;

      try {
        const { doc, filename } = await buildStockCountSheetPdf({
          rows: this.lines,

          venueId: this.venueId,

          generatedAt: new Date(),
        });

        doc.save(filename);
      } catch (error) {
        console.error("[STOCK COUNT] Count sheet generation failed:", error);

        this.error =
          error?.message || "Unable to generate the printable stock count sheet.";
      }
    },

    startCatalogueSubscription() {
      this.stopCatalogueSubscription();

      this.catalogueUnsubscribe = subscribeStockProductsForCount(
        (products) => {
          this.catalogue = Array.isArray(products) ? products : [];

          if (this.countStarted) {
            this.applyRecipeSales();
          }
        },

        (error) => {
          console.error("[STOCK COUNT] Catalogue listener failed:", error);
        }
      );
    },

    stopCatalogueSubscription() {
      if (typeof this.catalogueUnsubscribe === "function") {
        this.catalogueUnsubscribe();
      }

      this.catalogueUnsubscribe = null;
    },

    async loadExistingDraft() {
      if (!this.venueId) {
        return;
      }

      this.busyLoad = true;
      this.error = null;

      try {
        const [catalogue, recipes, draft] = await Promise.all([
          fetchStockProductsForCount(),

          fetchStockRecipes(),

          fetchDraftStockCount(this.venueId),
        ]);

        this.catalogue = Array.isArray(catalogue) ? catalogue : [];

        this.recipes = Array.isArray(recipes) ? recipes : [];

        if (!draft) {
          return;
        }

        this.draftCountId = draft.id;

        this.deliveryNoteIds = Array.isArray(draft.delivery_note_ids)
          ? draft.delivery_note_ids
          : [];

        this.internalTransferIds = Array.isArray(draft.internal_transfer_ids)
          ? draft.internal_transfer_ids
          : [];

        this.recipeSaleQuantities = this.recipeQuantitiesFromEntries(
          draft.recipe_sales_entries
        );

        this.lines = (Array.isArray(draft.lines) ? draft.lines : []).map((line) =>
          this.hydrateDraftLine(line)
        );

        this.applyRecipeSales();

        this.countStarted = true;

        this.successMessage = "Saved stock count draft loaded.";
      } catch (error) {
        console.error("[STOCK COUNT] Draft load failed:", error);

        this.error = error?.message || "Unable to load the saved stock-count draft.";
      } finally {
        this.busyLoad = false;
      }
    },

    hydrateDraftLine(line) {
      const variant = this.catalogue.find((product) => product.id === line.product_id);

      const source = variant || line;

      const orderUnit = String(source.order_unit ?? line.order_unit ?? "").trim();

      const stockUnit = String(
        source.stock_unit ?? line.stock_unit ?? line.unit_size ?? ""
      ).trim();

      const stockPerOrder = this.number(
        source.stock_units_per_order_unit ?? line.stock_units_per_order_unit
      );

      const salesPerStock = this.number(
        source.sales_units_per_stock_unit ?? line.sales_units_per_stock_unit
      );

      const unitCost = this.number(
        source.unit_cost ?? line.unit_cost_order_unit ?? line.unit_cost
      );

      return {
        product_key: line.product_key,

        product_id: line.product_id,

        product_name: line.product_name,

        product_group: line.product_group,

        supplier: String(source.supplier ?? line.supplier ?? "").trim(),

        order_unit: orderUnit,

        stock_unit: stockUnit,

        stock_units_per_order_unit: stockPerOrder,

        sales_units_per_stock_unit: salesPerStock,

        unit_cost_order_unit: unitCost,

        /*
         * Compatibility snapshots.
         */
        unit_size: stockUnit,

        unit_cost: unitCost,

        stock_units_per_unit: stockPerOrder,

        opening_count: this.number(line.opening_count),

        stock_in: this.number(line.stock_in),

        stock_in_from_deliveries: this.number(line.stock_in_from_deliveries),

        stock_in_from_transfers: this.number(line.stock_in_from_transfers),

        stock_out: this.number(line.stock_out),

        stock_out_from_transfers: this.number(line.stock_out_from_transfers),

        closing_count: this.number(line.closing_count),

        sales: this.number(line.sales),

        recipe_sales: this.number(line.recipe_sales),
      };
    },

    async newCount() {
      if (!this.venueId || this.busy) {
        return;
      }

      this.error = null;
      this.successMessage = null;
      this.busyLoad = true;

      try {
        const existingDraft = await fetchDraftStockCount(this.venueId);

        if (existingDraft) {
          this.busyLoad = false;

          await this.loadExistingDraft();

          return;
        }

        const [
          catalogue,
          recipes,
          previousCount,
          deliveryNotes,
          transfers,
        ] = await Promise.all([
          fetchStockProductsForCount(),

          fetchStockRecipes(),

          fetchLatestStockCount(this.venueId),

          fetchPendingStockDeliveryNotes(this.venueId),

          fetchPendingStockInternalTransfers(this.venueId),
        ]);

        this.catalogue = Array.isArray(catalogue) ? catalogue : [];

        this.recipes = Array.isArray(recipes) ? recipes : [];

        this.recipeSaleQuantities = {};

        if (!this.catalogue.length) {
          throw new Error("There are no products in the Stock Database.");
        }

        this.previousCount = previousCount;

        this.deliveryNoteIds = (Array.isArray(deliveryNotes) ? deliveryNotes : [])
          .map((note) => note.id)
          .filter(Boolean);

        this.internalTransferIds = (Array.isArray(transfers) ? transfers : [])
          .map((transfer) => transfer.id)
          .filter(Boolean);

        const countLines = [];
        const lineByKey = new Map();

        if (previousCount && Array.isArray(previousCount.lines)) {
          for (const previousLine of previousCount.lines) {
            const productName = String(previousLine.product_name ?? "").trim();

            const productGroup = String(previousLine.product_group ?? "").trim();

            if (!productName || !productGroup) {
              continue;
            }

            const productKey =
              previousLine.product_key || makeProductKey(productGroup, productName);

            if (lineByKey.has(productKey)) {
              continue;
            }

            const variant = this.findBestVariant({
              productKey,

              productId: previousLine.product_id,

              supplier: previousLine.supplier,
            });

            const line = this.makeCountLine({
              productKey,
              productName,
              productGroup,

              selectedVariant: variant,

              fallbackLine: previousLine,

              openingCount: this.number(previousLine.closing_count),
            });

            countLines.push(line);

            lineByKey.set(productKey, line);
          }
        }

        /*
         * Delivery notes:
         *
         * New workflow:
         * Order Units are converted into Stock Units.
         *
         * Legacy workflow:
         * old delivery quantities were already inserted directly
         * into Stock In. The helper below detects the new
         * database-driven conversion safely.
         */
        for (const note of Array.isArray(deliveryNotes) ? deliveryNotes : []) {
          for (const movementLine of Array.isArray(note.lines) ? note.lines : []) {
            const line = this.ensureMovementLine(countLines, lineByKey, movementLine);

            if (!line) {
              continue;
            }

            line.stock_in_from_deliveries += this.deliveryStockUnits(line, movementLine);
          }
        }

        /*
         * Internal transfers are already physical Stock Units.
         */
        for (const transfer of Array.isArray(transfers) ? transfers : []) {
          const direction = String(transfer.direction ?? "")
            .trim()
            .toLowerCase();

          for (const movementLine of Array.isArray(transfer.lines)
            ? transfer.lines
            : []) {
            const line = this.ensureMovementLine(countLines, lineByKey, movementLine);

            if (!line) {
              continue;
            }

            const quantity = Math.abs(this.number(movementLine.quantity_transferred));

            if (direction === "out") {
              line.stock_out_from_transfers += quantity;
            } else {
              line.stock_in_from_transfers += quantity;
            }
          }
        }

        for (const line of countLines) {
          line.stock_in = this.stockInBaseline(line);

          line.stock_out = this.stockOutBaseline(line);
        }

        this.lines = countLines;

        this.draftCountId = "";
        this.countStarted = true;

        this.applyRecipeSales();
      } catch (error) {
        console.error("[STOCK COUNT] New count failed:", error);

        this.error = error?.message || "Unable to prepare the stock count.";

        this.clearCountState();
      } finally {
        this.busyLoad = false;
      }
    },

    deliveryStockUnits(line, movementLine) {
      const quantityDelivered = this.number(movementLine.quantity_delivered);

      /*
       * If a stored physical Stock Unit quantity exists,
       * use it directly.
       */
      if (
        Object.prototype.hasOwnProperty.call(
          movementLine,
          "quantity_delivered_stock_units"
        )
      ) {
        return this.number(movementLine.quantity_delivered_stock_units);
      }

      const lineConversion = this.number(line.stock_units_per_order_unit);

      const storedConversion =
        this.number(movementLine.stock_units_per_order_unit) ||
        this.number(movementLine.stock_units_per_unit);

      const movementOrderUnit = String(
        movementLine.order_unit || movementLine.unit_size || ""
      )
        .trim()
        .toLowerCase();

      const currentOrderUnit = String(line.order_unit || "")
        .trim()
        .toLowerCase();

      /*
       * The existing liquor-order backend preserves:
       *
       * unitSize   -> Order Unit
       * stockUnits -> Stock Units per Order Unit
       *
       * It then writes those as:
       *
       * unit_size
       * stock_units_per_unit
       *
       * If those match the current database conversion this is
       * a new-workflow delivery and can be converted safely.
       */
      const conversionMatches =
        storedConversion > 0 &&
        lineConversion > 0 &&
        Math.abs(storedConversion - lineConversion) < 0.0001;

      const orderUnitMatches =
        movementOrderUnit && currentOrderUnit && movementOrderUnit === currentOrderUnit;

      if (conversionMatches && orderUnitMatches) {
        return quantityDelivered * storedConversion;
      }

      /*
       * Legacy delivery note:
       * quantity_delivered was already treated as Stock Units.
       */
      return quantityDelivered;
    },

    findBestVariant({ productKey, productId = "", supplier = "" }) {
      const variants = this.catalogue.filter(
        (product) =>
          makeProductKey(product.product_group, product.item_name) === productKey
      );

      if (!variants.length) {
        return null;
      }

      let selected = variants.find((variant) => variant.id === productId);

      if (!selected && supplier) {
        selected = variants.find(
          (variant) =>
            String(variant.supplier ?? "")
              .trim()
              .toLowerCase() === String(supplier).trim().toLowerCase()
        );
      }

      return selected || variants[0];
    },

    ensureMovementLine(countLines, lineByKey, movementLine) {
      const productName = String(movementLine.product_name ?? "").trim();

      const productGroup = String(movementLine.product_group ?? "").trim();

      if (!productName || !productGroup) {
        return null;
      }

      const productKey =
        movementLine.product_key || makeProductKey(productGroup, productName);

      if (lineByKey.has(productKey)) {
        return lineByKey.get(productKey);
      }

      const variant = this.findBestVariant({
        productKey,

        productId: movementLine.product_id,

        supplier: movementLine.supplier,
      });

      const line = this.makeCountLine({
        productKey,
        productName,
        productGroup,

        selectedVariant: variant,

        fallbackLine: movementLine,

        openingCount: 0,
      });

      countLines.push(line);

      lineByKey.set(productKey, line);

      return line;
    },

    makeCountLine({
      productKey,
      productName,
      productGroup,
      selectedVariant = null,
      fallbackLine = null,
      openingCount = 0,
    }) {
      const source = selectedVariant || fallbackLine || {};

      const orderUnit = String(
        source.order_unit ?? fallbackLine?.order_unit ?? ""
      ).trim();

      const stockUnit = String(
        source.stock_unit ?? fallbackLine?.stock_unit ?? fallbackLine?.unit_size ?? ""
      ).trim();

      const stockPerOrder = this.number(
        source.stock_units_per_order_unit ??
          fallbackLine?.stock_units_per_order_unit ??
          (selectedVariant ? source.stock_units : 0)
      );

      const salesPerStock = this.number(
        source.sales_units_per_stock_unit ??
          fallbackLine?.sales_units_per_stock_unit ??
          (selectedVariant ? source.sales_units : 0)
      );

      const unitCost = this.number(
        source.unit_cost ?? fallbackLine?.unit_cost_order_unit ?? fallbackLine?.unit_cost
      );

      return {
        product_key: productKey,

        product_id: selectedVariant?.id || fallbackLine?.product_id || "",

        product_name: productName,

        product_group: productGroup,

        supplier: String(source.supplier ?? fallbackLine?.supplier ?? "").trim(),

        order_unit: orderUnit,

        stock_unit: stockUnit,

        stock_units_per_order_unit: stockPerOrder,

        sales_units_per_stock_unit: salesPerStock,

        unit_cost_order_unit: unitCost,

        unit_size: stockUnit,

        unit_cost: unitCost,

        stock_units_per_unit: stockPerOrder,

        opening_count: this.number(openingCount),

        stock_in: 0,

        stock_in_from_deliveries: 0,

        stock_in_from_transfers: 0,

        stock_out: 0,

        stock_out_from_transfers: 0,

        closing_count: 0,

        sales: 0,

        recipe_sales: 0,
      };
    },

    supplierOptionsForLine(line) {
      const options = this.catalogue
        .filter(
          (variant) =>
            makeProductKey(variant.product_group, variant.item_name) === line.product_key
        )
        .sort((a, b) =>
          String(a.supplier ?? "").localeCompare(String(b.supplier ?? ""), "en", {
            sensitivity: "base",
          })
        );

      if (
        line.product_id &&
        line.supplier &&
        !options.some((option) => option.id === line.product_id)
      ) {
        return [
          {
            id: line.product_id,

            supplier: line.supplier,

            product_group: line.product_group,

            item_name: line.product_name,

            order_unit: line.order_unit,

            stock_unit: line.stock_unit,

            stock_units_per_order_unit: line.stock_units_per_order_unit,

            sales_units_per_stock_unit: line.sales_units_per_stock_unit,

            unit_cost: line.unit_cost_order_unit,
          },

          ...options,
        ];
      }

      return options;
    },

    supplierChanged(line) {
      const selected = this.catalogue.find((product) => product.id === line.product_id);

      if (!selected) {
        return;
      }

      line.supplier = String(selected.supplier ?? "").trim();

      line.order_unit = String(selected.order_unit ?? "").trim();

      line.stock_unit = String(selected.stock_unit ?? "").trim();

      line.stock_units_per_order_unit = this.number(selected.stock_units_per_order_unit);

      line.sales_units_per_stock_unit = this.number(selected.sales_units_per_stock_unit);

      line.unit_cost_order_unit = this.number(selected.unit_cost);

      line.unit_size = line.stock_unit;

      line.unit_cost = line.unit_cost_order_unit;

      line.stock_units_per_unit = line.stock_units_per_order_unit;
    },

    availableProductsForGroup(groupName) {
      const existingKeys = new Set(this.lines.map((line) => line.product_key));

      const uniqueProducts = new Map();

      for (const product of this.catalogue) {
        if (product.product_group !== groupName) {
          continue;
        }

        const productName = String(product.item_name ?? "").trim();

        if (!productName) {
          continue;
        }

        const key = makeProductKey(groupName, productName);

        if (existingKeys.has(key)) {
          continue;
        }

        if (!uniqueProducts.has(key)) {
          uniqueProducts.set(key, {
            key,

            product_name: productName,

            product_group: groupName,
          });
        }
      }

      return Array.from(uniqueProducts.values()).sort((a, b) =>
        a.product_name.localeCompare(b.product_name, "en", {
          sensitivity: "base",
        })
      );
    },

    openAddLineModal(groupName) {
      this.addLineForm = {
        product_group: groupName,

        product_key: "",

        product_id: "",
      };

      this.addLineModalOpen = true;
    },

    addLineProductChanged() {
      this.addLineForm.product_id = "";
    },

    addSelectedLine() {
      if (!this.addLineValid) {
        return;
      }

      const variant = this.selectedAddLineVariant;

      const productName = String(variant.item_name ?? "").trim();

      const productGroup = String(variant.product_group ?? "").trim();

      const productKey = makeProductKey(productGroup, productName);

      if (this.lines.some((line) => line.product_key === productKey)) {
        this.error = `${productName} is already in this stock count.`;

        this.closeAddLineModal();

        return;
      }

      this.lines.push(
        this.makeCountLine({
          productKey,
          productName,
          productGroup,

          selectedVariant: variant,

          openingCount: 0,
        })
      );

      this.applyRecipeSales();

      this.closeAddLineModal();
    },

    closeAddLineModal() {
      this.addLineModalOpen = false;

      this.addLineForm = blankAddLineForm();
    },

    recipeQuantitiesFromEntries(entries) {
      const quantities = {};

      for (const entry of Array.isArray(entries) ? entries : []) {
        const recipeId = String(entry.recipe_id ?? "").trim();

        const quantity = this.number(entry.quantity_sold);

        if (recipeId && quantity > 0) {
          quantities[recipeId] = quantity;
        }
      }

      return quantities;
    },

    recipeSalesEntriesForSave() {
      return this.recipes
        .map((recipe) => ({
          recipe_id: recipe.id,

          recipe_name: String(recipe.recipe_name ?? "").trim(),

          quantity_sold: this.number(this.recipeSaleQuantities[recipe.id]),
        }))
        .filter((entry) => entry.recipe_id && entry.quantity_sold > 0);
    },

    recipeSalesChanged(quantities) {
      this.recipeSaleQuantities = {
        ...(quantities || {}),
      };

      this.applyRecipeSales();
    },

    applyRecipeSales() {
      const lineByKey = new Map(this.lines.map((line) => [line.product_key, line]));

      const missingProducts = new Set();

      this.recipeError = null;

      for (const line of this.lines) {
        line.recipe_sales = 0;
      }

      for (const recipe of this.recipes) {
        const quantitySold = this.number(this.recipeSaleQuantities[recipe.id]);

        if (quantitySold <= 0) {
          continue;
        }

        for (const ingredient of Array.isArray(recipe.ingredients)
          ? recipe.ingredients
          : []) {
          const productKey = String(ingredient.product_key ?? "").trim();

          const salesUnits = this.number(ingredient.selling_units);

          if (!productKey || salesUnits <= 0) {
            continue;
          }

          let line = lineByKey.get(productKey);

          if (!line) {
            const variant = this.findBestVariant({
              productKey,
            });

            if (!variant) {
              missingProducts.add(ingredient.product_name || productKey);

              continue;
            }

            line = this.makeCountLine({
              productKey,

              productName: String(ingredient.product_name ?? variant.item_name).trim(),

              productGroup: String(
                ingredient.product_group ?? variant.product_group
              ).trim(),

              selectedVariant: variant,

              openingCount: 0,
            });

            this.lines.push(line);

            lineByKey.set(productKey, line);
          }

          line.recipe_sales += quantitySold * salesUnits;
        }
      }

      if (missingProducts.size) {
        this.recipeError =
          "These recipe ingredients are no longer in the Stock Database: " +
          `${Array.from(missingProducts).join(", ")}.`;
      }
    },

    stockInBaseline(line) {
      return (
        this.number(line.stock_in_from_deliveries) +
        this.number(line.stock_in_from_transfers)
      );
    },

    stockOutBaseline(line) {
      return this.number(line.stock_out_from_transfers);
    },

    stockInTitle(line) {
      const delivery = this.number(line.stock_in_from_deliveries);

      const transfer = this.number(line.stock_in_from_transfers);

      if (delivery === 0 && transfer === 0) {
        return `Stock In (${line.stock_unit || "Stock Unit"})`;
      }

      return (
        `Delivery Notes: ${this.formatNumber(delivery)} ${line.stock_unit || ""} | ` +
        `Transfers In: ${this.formatNumber(transfer)} ${line.stock_unit || ""}`
      );
    },

    stockOutTitle(line) {
      const transfer = this.number(line.stock_out_from_transfers);

      if (transfer === 0) {
        return `Stock Out (${line.stock_unit || "Stock Unit"})`;
      }

      return `Transfers Out: ${this.formatNumber(transfer)} ${line.stock_unit || ""}`;
    },

    availableStock(line) {
      return (
        this.number(line.opening_count) +
        this.number(line.stock_in) -
        this.number(line.stock_out)
      );
    },

    consumption(line) {
      return this.availableStock(line) - this.number(line.closing_count);
    },

    totalSellingUnits(line) {
      return this.number(line.sales) + this.number(line.recipe_sales);
    },

    salesStockUnits(line) {
      const totalSalesUnits = this.totalSellingUnits(line);

      const salesUnitsPerStockUnit = this.number(line.sales_units_per_stock_unit);

      if (salesUnitsPerStockUnit <= 0) {
        return 0;
      }

      return totalSalesUnits / salesUnitsPerStockUnit;
    },

    salesConversionTitle(line) {
      const conversion = this.number(line.sales_units_per_stock_unit);

      if (conversion <= 0) {
        return "No Sales Units conversion configured.";
      }

      return (
        `${this.formatInputNumber(conversion)} Sales Units = 1 ${
          line.stock_unit || "Stock Unit"
        }. ` +
        `Direct: ${this.formatNumber(line.sales)} | ` +
        `Recipe: ${this.formatNumber(line.recipe_sales)} | ` +
        `Total: ${this.formatNumber(this.totalSellingUnits(line))}`
      );
    },

    recipeSalesTitle(line) {
      const conversion = this.number(line.sales_units_per_stock_unit);

      const converted = conversion > 0 ? this.number(line.recipe_sales) / conversion : 0;

      return (
        `Recipe Sales Units: ${this.formatNumber(line.recipe_sales)}. ` +
        `Converted usage: ${this.formatNumber(converted)} ${
          line.stock_unit || "Stock Units"
        }.`
      );
    },

    varianceUnits(line) {
      return this.salesStockUnits(line) - this.consumption(line);
    },

    varianceValue(line) {
      return this.varianceUnits(line) * this.stockUnitCost(line);
    },

    linesForSave() {
      return this.lines.map((line) => {
        const stockIn = this.number(line.stock_in);

        const stockOut = this.number(line.stock_out);

        const stockInBaseline = this.stockInBaseline(line);

        const stockOutBaseline = this.stockOutBaseline(line);

        return {
          product_key: line.product_key,

          product_id: line.product_id,

          product_name: line.product_name,

          product_group: line.product_group,

          supplier: line.supplier,

          order_unit: line.order_unit,

          stock_unit: line.stock_unit,

          stock_units_per_order_unit: this.number(line.stock_units_per_order_unit),

          sales_units_per_stock_unit: this.number(line.sales_units_per_stock_unit),

          unit_cost_order_unit: this.number(line.unit_cost_order_unit),

          unit_cost_stock_unit: this.stockUnitCost(line),

          unit_size: line.stock_unit,

          unit_cost: this.number(line.unit_cost_order_unit),

          stock_units_per_unit: this.number(line.stock_units_per_order_unit),

          opening_count: this.number(line.opening_count),

          stock_in: stockIn,

          stock_in_from_deliveries: this.number(line.stock_in_from_deliveries),

          stock_in_from_transfers: this.number(line.stock_in_from_transfers),

          stock_in_overridden: Math.abs(stockIn - stockInBaseline) > 0.0001,

          stock_out: stockOut,

          stock_out_from_transfers: this.number(line.stock_out_from_transfers),

          stock_out_overridden: Math.abs(stockOut - stockOutBaseline) > 0.0001,

          closing_count: this.number(line.closing_count),

          sales: this.number(line.sales),

          recipe_sales: this.number(line.recipe_sales),

          total_sales: this.totalSellingUnits(line),

          sales_stock_units: this.salesStockUnits(line),

          available_stock: this.availableStock(line),

          consumption: this.consumption(line),

          variance_units: this.varianceUnits(line),

          variance_value: this.varianceValue(line),
        };
      });
    },

    async saveDraft() {
      if (!this.venueId || !this.lines.length || !this.countValid || this.busy) {
        return;
      }

      this.busySave = true;
      this.error = null;
      this.successMessage = null;

      try {
        const savedId = await saveStockCountDraft({
          venueId: this.venueId,

          countId: this.draftCountId,

          lines: this.linesForSave(),

          deliveryNoteIds: this.deliveryNoteIds,

          internalTransferIds: this.internalTransferIds,

          recipeSalesEntries: this.recipeSalesEntriesForSave(),
        });

        this.draftCountId = savedId;

        this.successMessage = "Stock count draft saved. You can continue editing it.";
      } catch (error) {
        console.error("[STOCK COUNT] Save draft failed:", error);

        this.error = error?.message || "Unable to save the stock-count draft.";
      } finally {
        this.busySave = false;
      }
    },

    async finaliseCount() {
      if (!this.venueId || !this.lines.length || !this.countValid || this.busy) {
        return;
      }

      const confirmed = window.confirm(
        "Finalise this stock count?\n\n" +
          "Once finalised it cannot be edited. " +
          "Its Closing Count will become the Opening Count " +
          "for the next stock count."
      );

      if (!confirmed) {
        return;
      }

      this.busyFinalise = true;
      this.error = null;
      this.successMessage = null;

      try {
        const savedId = await saveStockCountDraft({
          venueId: this.venueId,

          countId: this.draftCountId,

          lines: this.linesForSave(),

          deliveryNoteIds: this.deliveryNoteIds,

          internalTransferIds: this.internalTransferIds,

          recipeSalesEntries: this.recipeSalesEntriesForSave(),
        });

        await finaliseStockCount({
          venueId: this.venueId,

          countId: savedId,
        });

        this.clearCountState();

        this.successMessage =
          "Stock count finalised successfully. It is now locked and available in Stock Reports.";
      } catch (error) {
        console.error("[STOCK COUNT] Finalise failed:", error);

        this.error = error?.message || "Unable to finalise the stock count.";
      } finally {
        this.busyFinalise = false;
      }
    },

    async cancelCount() {
      if (!this.countStarted) {
        return;
      }

      if (this.draftCountId) {
        const confirmed = window.confirm(
          "Discard changes made since the last Save Draft?"
        );

        if (!confirmed) {
          return;
        }

        await this.loadExistingDraft();

        this.successMessage = "Unsaved changes discarded.";

        return;
      }

      const confirmed = window.confirm(
        "Cancel this stock count? Any figures entered will be lost."
      );

      if (!confirmed) {
        return;
      }

      this.clearCountState();
    },

    clearCountState() {
      this.lines = [];
      this.previousCount = null;
      this.draftCountId = "";

      this.deliveryNoteIds = [];
      this.internalTransferIds = [];

      this.recipes = [];
      this.recipeSaleQuantities = {};

      this.countStarted = false;

      this.addLineModalOpen = false;

      this.addLineForm = blankAddLineForm();

      this.error = null;
      this.recipeError = null;
    },

    varianceClass(value) {
      const n = this.number(value);

      if (n < 0) {
        return "red-text text-darken-2";
      }

      if (n > 0) {
        return "green-text text-darken-2";
      }

      return "grey-text text-darken-2";
    },

    formatNumber(value) {
      return this.number(value).toLocaleString("en-GB", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });
    },

    formatInputNumber(value) {
      const n = this.number(value);

      return Number.isInteger(n) ? String(n) : String(Math.round(n * 10000) / 10000);
    },

    money(value) {
      return new Intl.NumberFormat("en-GB", {
        style: "currency",
        currency: "GBP",
      }).format(this.number(value));
    },
  },
};
</script>

<style scoped>
.stock-count-view {
  width: 100%;
  min-width: 0;
}

/* =========================================================
   Toolbar
   ========================================================= */

.count-toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 16px;
}

.toolbar-actions {
  display: flex;
  align-items: center;
  gap: 8px;
}

.count-heading-row {
  display: flex;
  align-items: center;
  gap: 9px;
}

.count-heading {
  color: #263238;
  font-size: 1.2rem;
  font-weight: 600;
}

.draft-chip {
  display: inline-flex;
  align-items: center;
  min-height: 23px;
  padding: 0 8px;
  border-radius: 12px;
  background-color: #fff3e0;
  color: #e65100;
  font-size: 0.72rem;
  font-weight: 600;
}

.no-venue-message {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 14px;
  border-radius: 6px;
  background-color: #fff8e1;
  color: #795548;
}

.count-information {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 14px;
  padding: 9px 11px;
  border-radius: 6px;
  background-color: #eceff1;
  color: #455a64;
  font-size: 0.85rem;
}

.error-box {
  margin-bottom: 14px;
  padding: 10px 12px;
  border: 1px solid #ef9a9a;
  border-radius: 6px;
  background-color: #ffebee;
  color: #c62828;
  font-weight: 600;
}

.success-box {
  margin-bottom: 14px;
  padding: 10px 12px;
  border: 1px solid #a5d6a7;
  border-radius: 6px;
  background-color: #e8f5e9;
  color: #2e7d32;
  font-weight: 600;
}

.count-empty {
  padding: 24px 10px;
  text-align: center;
  color: #757575;
}

/* =========================================================
   Stock count table
   ========================================================= */

.stock-count-table-wrap {
  width: 100%;
  min-width: 0;
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
}

.stock-count-table {
  width: 100%;
  min-width: 1120px;
  table-layout: fixed;
  border-collapse: collapse;
}

.stock-count-table th:nth-child(1),
.stock-count-table td:nth-child(1) {
  width: 18%;
}

.stock-count-table th:nth-child(2),
.stock-count-table td:nth-child(2) {
  width: 14%;
}

.stock-count-table th:nth-child(3),
.stock-count-table td:nth-child(3) {
  width: 10%;
}

.stock-count-table th:nth-child(4),
.stock-count-table td:nth-child(4) {
  width: 8%;
}

.stock-count-table th:nth-child(5),
.stock-count-table td:nth-child(5) {
  width: 8%;
}

.stock-count-table th:nth-child(6),
.stock-count-table td:nth-child(6) {
  width: 10%;
}

.stock-count-table th:nth-child(7),
.stock-count-table td:nth-child(7) {
  width: 8%;
}

.stock-count-table th:nth-child(8),
.stock-count-table td:nth-child(8) {
  width: 8%;
}

.stock-count-table th:nth-child(9),
.stock-count-table td:nth-child(9) {
  width: 8%;
}

.stock-count-table th:nth-child(10),
.stock-count-table td:nth-child(10) {
  width: 8%;
}

.stock-count-table th {
  box-sizing: border-box;
  padding: 9px 6px;
  color: #455a64;
  font-size: 0.82rem;
  font-weight: 600;
  line-height: 1.15;
  white-space: normal;
  vertical-align: bottom;
}

.stock-count-table td {
  box-sizing: border-box;
  padding: 8px 6px;
  vertical-align: middle;
}

.product-name {
  overflow: hidden;
  font-size: 0.9rem;
  font-weight: 600;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.stock-unit-chip {
  display: inline-block;
  margin-left: 5px;
  padding: 2px 5px;
  border-radius: 4px;
  background: #eceff1;
  color: #607d8b;
  font-size: 0.68rem;
  font-weight: 500;
}

.stock-count-table .product-group-row {
  background-color: #455a64 !important;
}

.stock-count-table .product-group-row td {
  width: auto !important;
  padding: 8px 10px;
  color: #ffffff;
}

.product-group-row td:first-child {
  width: 100% !important;
}

.product-group-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  width: 100%;
}

.product-group-title {
  display: flex;
  align-items: center;
  gap: 7px;
  min-width: 0;
  font-size: 0.95rem;
}

.product-count {
  font-size: 0.78rem;
  font-weight: 400;
  opacity: 0.8;
}

/*
 * This button is intentionally smaller than the normal
 * globally styled .btn-small because it lives inside the
 * stock-group table heading.
 */
.group-add-button {
  flex: 0 0 auto;
  display: inline-flex !important;
  align-items: center !important;
  justify-content: center !important;
  gap: 4px !important;
  min-width: 88px;
  min-height: 28px !important;
  height: 28px !important;
  margin: 0 !important;
  padding: 0 9px !important;
  border: 1px solid #00796b !important;
  border-radius: 4px !important;
  background-color: #00897b !important;
  color: #ffffff !important;
  box-shadow: none !important;
  line-height: 28px !important;
  font-size: 0.75rem !important;
  font-weight: 500 !important;
}

.group-add-button:hover {
  background-color: #00796b !important;
}

.group-add-button i {
  width: auto !important;
  height: 18px !important;
  margin: 0 !important;
  color: #ffffff !important;
  line-height: 18px !important;
  font-size: 17px !important;
}

.group-add-button:disabled {
  background-color: #80cbc4 !important;
  color: rgba(255, 255, 255, 0.75) !important;
}

.empty-group-row td {
  width: 100% !important;
  padding: 10px !important;
  background-color: #fafafa;
  color: #9e9e9e;
  font-size: 0.82rem;
  font-style: italic;
}

.supplier-select {
  box-sizing: border-box;
  width: 100%;
  height: 34px;
  margin: 0;
  padding: 0 5px;
  border: 1px solid #bdbdbd;
  border-radius: 4px;
  background-color: #ffffff;
  color: #424242;
  font-size: 0.8rem;
}

.supplier-select:focus {
  outline: none;
  border-color: #00897b;
  box-shadow: 0 0 0 1px #00897b;
}

.count-input {
  box-sizing: border-box !important;
  width: 100% !important;
  height: 34px !important;
  margin: 0 !important;
  padding: 0 5px !important;
  border: 1px solid #bdbdbd !important;
  border-radius: 4px !important;
  background-color: #ffffff !important;
  box-shadow: none !important;
  text-align: right;
  font-size: 0.82rem;
}

.count-input:focus {
  border: 1px solid #00897b !important;
  box-shadow: 0 0 0 1px #00897b !important;
}

.readonly-input {
  background-color: #eeeeee !important;
  color: #616161;
}

.sales-input {
  font-weight: 600;
}

.variance-result {
  overflow: hidden;
  font-size: 0.82rem;
  font-weight: 700;
  text-overflow: ellipsis;
  white-space: nowrap;
}

/* =========================================================
   Footer
   ========================================================= */

.count-footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  margin-top: 18px;
}

.footer-actions {
  display: flex;
  align-items: center;
  gap: 8px;
}

.variance-key {
  display: flex;
  align-items: center;
  gap: 18px;
  font-size: 0.8rem;
  font-weight: 600;
}

.movement-info {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 12px;
  padding: 9px 11px;
  border-radius: 5px;
  background-color: #e0f2f1;
  color: #00695c;
  font-size: 0.8rem;
}

.supplier-warning {
  display: flex;
  align-items: center;
  gap: 7px;
  margin-top: 12px;
  color: #ef6c00;
  font-size: 0.85rem;
}

/* =========================================================
   Add-line modal
   ========================================================= */

.modal-overlay {
  position: fixed;
  z-index: 10000;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
  background: rgba(0, 0, 0, 0.45);
}

.add-line-modal {
  box-sizing: border-box;
  width: min(720px, 100%);
  max-height: 90vh;
  overflow-y: auto;
  padding: 24px;
  border-radius: 10px;
  background: #ffffff;
  box-shadow: 0 8px 30px rgba(0, 0, 0, 0.25);
}

.modal-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 20px;
  margin-bottom: 24px;
}

.modal-title {
  color: #263238;
  font-size: 1.5rem;
  font-weight: 600;
}

/*
 * Custom icon-only modal close button.
 */
.modal-close-button {
  flex: 0 0 auto;
  min-width: 38px !important;
  width: 38px !important;
  min-height: 38px !important;
  height: 38px !important;
  padding: 0 !important;
  border: 0 !important;
  border-radius: 50% !important;
  background: transparent !important;
  box-shadow: none !important;
  cursor: pointer;
  color: #616161;
}

.no-products-message {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 16px;
  padding: 10px 12px;
  border-radius: 5px;
  background-color: #eceff1;
  color: #546e7a;
  font-size: 0.85rem;
}

.modal-form-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 18px;
}

.modal-field label {
  display: block;
  position: static;
  margin: 0 0 7px;
  color: #607d8b;
  font-size: 0.82rem;
  font-weight: 600;
  transform: none;
}

.modal-select {
  box-sizing: border-box;
  width: 100%;
  height: 44px;
  padding: 0 10px;
  border: 1px solid #bdbdbd;
  border-radius: 4px;
  background: #ffffff;
  font-size: 15px;
}

.modal-select:disabled {
  background-color: #eeeeee;
  color: #9e9e9e;
}

.selected-product-details {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 12px;
  margin-top: 22px;
  padding: 14px;
  border-radius: 6px;
  background-color: #eceff1;
}

.selected-product-details > div {
  display: flex;
  flex-direction: column;
  gap: 3px;
}

.detail-label {
  color: #78909c;
  font-size: 0.75rem;
}

.modal-actions {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 24px;
}

/* =========================================================
   Responsive component-specific layout
   ========================================================= */

@media only screen and (max-width: 1100px) {
  .stock-count-table th {
    padding: 7px 4px;
    font-size: 0.74rem;
  }

  .stock-count-table td {
    padding: 6px 4px;
  }

  .count-input,
  .supplier-select {
    height: 32px;
    font-size: 0.75rem;
  }

  .product-name,
  .variance-result {
    font-size: 0.76rem;
  }
}

@media only screen and (max-width: 600px) {
  .count-toolbar {
    display: block;
  }

  .toolbar-actions {
    display: block;
  }

  /*
   * Width of normal .btn controls comes from coredeck-ui.css.
   */
  .toolbar-actions .btn {
    margin-top: 12px !important;
  }

  .stock-count-table th {
    padding: 5px 2px;
    font-size: 0.62rem;
  }

  .stock-count-table td {
    padding: 5px 2px;
  }

  .product-name {
    font-size: 0.68rem;
  }

  .stock-unit-chip {
    display: block;
    width: fit-content;
    margin: 3px 0 0;
  }

  .count-input,
  .supplier-select {
    height: 30px;
    font-size: 0.67rem;
  }

  .variance-result {
    font-size: 0.65rem;
  }

  .count-footer {
    display: block;
  }

  .footer-actions {
    display: block;
  }

  /*
   * Standard .btn mobile width is global.
   * btn-flat is explicitly full width in this component.
   */
  .footer-actions .btn-flat {
    width: 100% !important;
    margin: 4px 0 !important;
  }

  .variance-key {
    display: block;
    margin-top: 14px;
  }

  .variance-key span {
    display: block;
    margin-top: 4px;
  }

  .add-line-modal {
    padding: 18px;
  }

  .modal-form-grid {
    grid-template-columns: 1fr;
  }

  .selected-product-details {
    grid-template-columns: 1fr 1fr;
  }
}
</style>
